/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import config from "../config/appCardList.config.json";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_HOLDER_POLICY_LIST } from "../graphql";
import { VApplication } from "../../../application";
import moment from "moment";
import { useUserId } from "../provider";

import { toHeaderCase } from "js-convert-case";

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IGetCredentialsListProps {
  offset?: number;
  size?: number;
  criteria?: any;
}

export interface IAppCardListMethods extends IPluginMethodMap {
  getAppCardListProps(args: IGetCredentialsListProps): Promise<any>;
}

export class AppCardListService implements IAgentPlugin {
  readonly methods: IAppCardListMethods;

  constructor() {
    this.methods = {
      getAppCardListProps: this.getProps.bind(this),
    };
  }

  async getProps(args: IGetCredentialsListProps) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    const { userId } = useUserId();
    try {
      if (client) {
        const res = await client.query({
          query: GET_HOLDER_POLICY_LIST,
          variables: {
            getListOfPoliciesInput2: {
              skip: args?.offset ?? 0,
              limit: args?.size ?? 10,
              where: [
                {
                  column: "purchasedBy",
                  op: "Equal",
                  value: [userId],
                },
              ],
              order: [
                {
                  column: "purchasedAt",
                  direction: "DESC",
                },
              ],
            },
          },
          fetchPolicy: "network-only",
        });
        if (res?.data?.getListOfPolicies) {
          let _templateData = this.formatTemplateList(
            res?.data?.getListOfPolicies?.data
          );
          return {
            appCardList: _templateData,
            isEmpty: res?.data?.getListOfPolicies?.data?.length <= 0,
            // studentGroup: holderGroups,
            activePage: res?.data?.getListOfPolicies?.currentPage,
            pageSize: res?.data?.getListOfPolicies?.pageSize,
            totalCount: res?.data?.getListOfPolicies?.totalCount,
            totalPages: res?.data?.getListOfPolicies?.totalPages,
          };
        }
      }
    } catch (err: any) {
      console.log(`error in table data`, err);
      return {
        isEmpty: true,
      };
    }
  }

  private formatTemplateList(data: any) {
    let _rowData: any =
      Array.isArray(data) &&
      data.map((item: any) => {
        return {
          rowData: item,
          id: item?.policyNumber,
          policyId: item?.policyId,
          vuid: item?.purchasedBy?.vuid,
          appCardImage: item?.logo ?? config?.icon,
          appCardTitle: item?.productDefination
            ? `Policy Option ${toHeaderCase(item?.productDefination)}`
            : "",
          appCardDescription: item?.createdAt
            ? moment(item?.createdAt).format("DD/MM/YYYY")
            : "",
          appCardList: [
            {
              name: "Policy No",
              value: item?.policyNumber,
            },
          ],
        };
      });

    return _rowData;
  }
}
